import type { AppError, Result } from '@aibi/shared';
import { z } from 'zod';
import { sendJson } from '../../api/http-client';
import type { ApiClientOptions } from './auth-api';

export const AI_SETTINGS_ROUTES = {
  settings: '/api/admin/ai',
  test: '/api/admin/ai/test',
  workflowExample: '/api/admin/ai/workflow-example',
} as const;

const AIStatusSchema = z.object({
  provider: z.string(),
  state: z.string(),
  checkedAt: z.string().nullable(),
});

export const AI_PROVIDERS = ['unconfigured', 'n8n', 'azure-ai'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];
export type AzureAuthType = 'api-key' | 'entra-sp';
export const AZURE_APIS = ['chat-completions', 'anthropic-messages'] as const;
export type AzureApiKind = (typeof AZURE_APIS)[number];

const AzureSettingsSchema = z.object({
  endpointConfigured: z.boolean(),
  host: z.string().nullable(),
  model: z.string().nullable(),
  api: z.enum(AZURE_APIS),
  authType: z.enum(['api-key', 'entra-sp']),
  tenantId: z.string().nullable(),
  clientId: z.string().nullable(),
  secretConfigured: z.boolean(),
});
export type AzureSettings = z.infer<typeof AzureSettingsSchema>;

const AiSettingsSchema = z.object({
  provider: z.enum(AI_PROVIDERS),
  source: z.enum(['env', 'app']),
  webhookConfigured: z.boolean(),
  tokenConfigured: z.boolean(),
  host: z.string().nullable(),
  azure: AzureSettingsSchema.nullable(),
  status: AIStatusSchema,
});
export type AiSettings = z.infer<typeof AiSettingsSchema>;

const AiTestSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
  hint: z.string().nullable(),
  status: AIStatusSchema,
});
export type AiTestResult = z.infer<typeof AiTestSchema>;

export interface AzureSettingsUpdate {
  /** Omitido: se conserva el endpoint guardado. */
  readonly endpoint?: string;
  readonly model: string;
  readonly api: AzureApiKind;
  readonly authType: AzureAuthType;
  readonly tenantId?: string;
  readonly clientId?: string;
  /** Clave de API o secreto del service principal; omitido: se conserva si el destino no cambia. */
  readonly secret?: string;
}

export interface AiSettingsUpdate {
  readonly provider: AiProvider;
  /** Omitida: se conserva la URL guardada. */
  readonly webhookUrl?: string;
  /** Omitido: se conserva el token guardado. */
  readonly authToken?: string;
  readonly clearToken?: boolean;
  readonly azure?: AzureSettingsUpdate;
}

/**
 * Configuracion del asistente de IA. El servidor nunca devuelve URLs completas, tokens
 * ni claves; el navegador nunca llama a n8n ni a Azure directamente.
 */
export interface AiSettingsApi {
  get(): Promise<Result<AiSettings, AppError>>;
  save(update: AiSettingsUpdate): Promise<Result<AiSettings, AppError>>;
  test(): Promise<Result<AiTestResult, AppError>>;
  /** URL de descarga del workflow de ejemplo (GET con la cookie de sesion). */
  readonly workflowExampleUrl: string;
}

/**
 * @example
 * const ai = createAiSettingsApi({ baseUrl: '', fetch: window.fetch.bind(window) });
 * await ai.save({ provider: 'n8n', webhookUrl, authToken });
 */
export function createAiSettingsApi({ baseUrl, fetch }: ApiClientOptions): AiSettingsApi {
  return {
    get: () => sendJson(fetch, 'GET', `${baseUrl}${AI_SETTINGS_ROUTES.settings}`, AiSettingsSchema),
    save: (update) =>
      sendJson(fetch, 'PUT', `${baseUrl}${AI_SETTINGS_ROUTES.settings}`, AiSettingsSchema, {
        body: update,
      }),
    test: () => sendJson(fetch, 'POST', `${baseUrl}${AI_SETTINGS_ROUTES.test}`, AiTestSchema),
    workflowExampleUrl: `${baseUrl}${AI_SETTINGS_ROUTES.workflowExample}`,
  };
}
